import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { useState } from "react";
import { useFgp } from "@/context/FgpContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { EmptyState } from "@/components/PageHeader";
import { LinkContactsDialog, AddTaskDialog, EditPersonDialog } from "@/components/EntityDialogs";
import {
  ArrowLeft, Mail, Phone, MapPin, Calendar, FileText, CheckSquare,
  GitBranch, Mic, Contact as ContactIcon, Plus, Link2, Edit,
} from "lucide-react";
import { motion } from "framer-motion";

export default function PersonDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openVoice } = useOutletContext();
  const { getPerson, documents, tasks, relationships, events, getContactsForPerson } = useFgp();
  const person = getPerson(id);
  const [linkOpen, setLinkOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  if (!person) return <EmptyState title="Person not found" hint="This person may have been removed." />;

  const personDocs = documents.filter((d) => d.person === id);
  const personTasks = tasks.filter((t) => t.person.toLowerCase().includes(person.name.split(" ")[0].toLowerCase()) || t.person === person.role);
  const rels = relationships.filter((r) => r.source === id || r.target === id);
  const personEvents = events.filter((e) => e.person.includes(person.name.split(" ")[0]) || e.person === "Household");
  const personContacts = getContactsForPerson(id);

  return (
    <div>
      <button onClick={() => navigate("/app/people")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6" data-testid="back-to-people">
        <ArrowLeft className="h-4 w-4" /> Back to People
      </button>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-6">
        <div className="h-24 bg-gradient-to-r from-neutral-600 to-neutral-500" />
        <div className="px-6 sm:px-8 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12">
            <Avatar className="h-24 w-24 ring-4 ring-white shadow-lg">
              <AvatarImage src={person.avatar} alt={person.name} />
              <AvatarFallback className="text-2xl">{person.name[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1 sm:pb-1">
              <h1 className="font-heading text-2xl font-semibold text-slate-900">{person.name}</h1>
              <p className="text-sm text-slate-500">{person.title}</p>
            </div>
            <div className="flex gap-2 sm:pb-1">
              <Button variant="outline" className="gap-2" data-testid="person-edit-detail" onClick={() => setEditOpen(true)}><Edit className="h-4 w-4" /> Edit</Button>
              <Button variant="outline" className="gap-2" data-testid="person-note" onClick={openVoice}><Mic className="h-4 w-4" /> Note</Button>
              <span className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium ${person.role === "Principal" ? "bg-neutral-50 text-neutral-700" : "bg-slate-100 text-slate-600"}`}>{person.role}</span>
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            {person.email !== "—" && <div className="flex items-center gap-2 text-slate-600"><Mail className="h-4 w-4 text-slate-400" /> {person.email}</div>}
            {person.phone !== "—" && <div className="flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4 text-slate-400" /> {person.phone}</div>}
            {person.location && person.location !== "—" && <div className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4 text-slate-400" /> {person.location}</div>}
            {person.dob && <div className="flex items-center gap-2 text-slate-600"><Calendar className="h-4 w-4 text-slate-400" /> Born {new Date(person.dob).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>}
          </div>

          {person.tags?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {person.tags.map((t) => <span key={t} className="text-xs font-medium bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">{t}</span>)}
            </div>
          )}
        </div>
      </motion.div>

      <Tabs defaultValue="documents" className="w-full">
        <TabsList className="bg-white border border-slate-200 p-1 h-auto flex-wrap">
          <TabsTrigger value="documents" data-testid="tab-documents" className="gap-1.5 data-[state=active]:bg-neutral-600 data-[state=active]:text-white"><FileText className="h-4 w-4" /> Documents</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts" className="gap-1.5 data-[state=active]:bg-neutral-600 data-[state=active]:text-white"><ContactIcon className="h-4 w-4" /> Contacts</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks" className="gap-1.5 data-[state=active]:bg-neutral-600 data-[state=active]:text-white"><CheckSquare className="h-4 w-4" /> Tasks</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events" className="gap-1.5 data-[state=active]:bg-neutral-600 data-[state=active]:text-white"><Calendar className="h-4 w-4" /> Events</TabsTrigger>
          <TabsTrigger value="relationships" data-testid="tab-relationships" className="gap-1.5 data-[state=active]:bg-neutral-600 data-[state=active]:text-white"><GitBranch className="h-4 w-4" /> Relationships</TabsTrigger>
        </TabsList>

        <TabsContent value="documents" className="mt-4">
          {personDocs.length ? (
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {personDocs.map((d) => (
                <div key={d.id} className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3"><FileText className="h-4 w-4 text-slate-400" /><div><p className="text-sm font-medium text-slate-800">{d.name}</p><p className="text-xs text-slate-400">{d.type} · expires {d.expiry}</p></div></div>
                  <StatusBadge status={d.status} />
                </div>
              ))}
            </div>
          ) : <EmptyState icon={FileText} title="No documents" hint="No documents are linked to this person yet." />}
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" onClick={() => setLinkOpen(true)} data-testid="link-contacts-btn" className="gap-1.5"><Link2 className="h-4 w-4" /> Link contacts</Button>
          </div>
          {personContacts.length ? (
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {personContacts.map((c) => (
                <div key={c.id} className="flex items-center justify-between px-5 py-4" data-testid={`person-contact-${c.id}`}>
                  <div className="flex items-center gap-3"><ContactIcon className="h-4 w-4 text-slate-400" /><div><p className="text-sm font-medium text-slate-800">{c.name}</p><p className="text-xs text-slate-400">{c.category} · {c.role}</p></div></div>
                  <div className="text-right hidden sm:block"><p className="text-xs text-slate-500">{c.phone}</p><p className="text-xs text-slate-400">{c.email}</p></div>
                </div>
              ))}
            </div>
          ) : <EmptyState icon={ContactIcon} title="No linked contacts" hint="Use 'Link contacts' to connect doctors, schools and providers to this person." />}
        </TabsContent>

        <TabsContent value="tasks" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" onClick={() => setTaskOpen(true)} data-testid="person-add-task" className="gap-1.5"><Plus className="h-4 w-4" /> Add task</Button>
          </div>
          {personTasks.length ? (
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {personTasks.map((t) => (
                <div key={t.id} className="flex items-center justify-between px-5 py-4">
                  <div><p className="text-sm font-medium text-slate-800">{t.title}</p><p className="text-xs text-slate-400">Due {t.due} · {t.assignee}</p></div>
                  <StatusBadge status={t.status} />
                </div>
              ))}
            </div>
          ) : <EmptyState icon={CheckSquare} title="No tasks" hint="Nothing assigned related to this person." />}
        </TabsContent>

        <TabsContent value="events" className="mt-4">
          {personEvents.length ? (
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {personEvents.map((e) => (
                <div key={e.id} className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-3"><Calendar className="h-4 w-4 text-slate-400" /><div><p className="text-sm font-medium text-slate-800">{e.title}</p><p className="text-xs text-slate-400">{e.date} · {e.time}</p></div></div>
                  <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{e.category}</span>
                </div>
              ))}
            </div>
          ) : <EmptyState icon={Calendar} title="No events" />}
        </TabsContent>

        <TabsContent value="relationships" className="mt-4">
          {rels.length ? (
            <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
              {rels.map((r, i) => {
                const otherId = r.source === id ? r.target : r.source;
                const other = getPerson(otherId);
                if (!other) return null;
                return (
                  <button key={i} onClick={() => navigate(`/app/people/${other.id}`)} className="w-full flex items-center gap-3 px-5 py-4 hover:bg-slate-50 text-left">
                    <Avatar className="h-9 w-9"><AvatarImage src={other.avatar} /><AvatarFallback>{other.name[0]}</AvatarFallback></Avatar>
                    <div className="flex-1"><p className="text-sm font-medium text-slate-800">{other.name}</p><p className="text-xs text-slate-400">{r.label}</p></div>
                    <span className="text-xs text-slate-400">{r.type}</span>
                  </button>
                );
              })}
            </div>
          ) : <EmptyState icon={GitBranch} title="No relationships" />}
        </TabsContent>
      </Tabs>

      <LinkContactsDialog open={linkOpen} onOpenChange={setLinkOpen} personId={id} />
      <AddTaskDialog open={taskOpen} onOpenChange={setTaskOpen} defaultPerson={person.name.split(" ")[0]} />
      <EditPersonDialog open={editOpen} onOpenChange={setEditOpen} person={person} />
    </div>
  );
}
